import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Button from '@components/ui/Button';
import Loading from '@components/ui/Loading';
import { useAPI } from '@hooks/useAPI';
import { getNews, getCountrys } from '@services/api/news';

const InternationalPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  // 获取国家列表
  const { data: countryList, loading: countriesLoading } = useAPI(() => getCountrys(), [i18n.language]);

  // 获取资讯列表
  const { data: newsData, loading: newsLoading, error: newsError, refetch } = useAPI(() => {
    const params = {
      page: currentPage,
      pageSize
    };
    // 只有当选择了特定国家时，才添加countryId参数
    if (selectedCountry !== 'all') {
      params.countryId = selectedCountry;
    }
    return getNews(params);
  }, [selectedCountry, currentPage, i18n.language]);

  // 处理国家选择
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setCurrentPage(1); // 重置到第一页
  };

  // 处理分页
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 渲染国家选择按钮
  const renderCountryButtons = () => {
    return (
      <div className="flex flex-wrap gap-2">
        {/* 全部国家选项 */}
        <Button
          key="all"
          variant={selectedCountry === 'all' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => handleCountrySelect('all')}
          className={`${selectedCountry === 'all' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white border-gray-300 hover:bg-gray-50'} px-4 py-2 whitespace-nowrap`}
        >
          {t('international.countrySelect.allCountries')}
        </Button>
        
        {/* 从API获取的国家列表 */}
        {countryList && countryList.map((country) => (
          <Button
            key={country.countryId || country.countryName}
            variant={selectedCountry === (country.countryId || country.countryName) ? 'primary' : 'outline'}
            size="sm"
            onClick={() => handleCountrySelect(country.countryId || country.countryName)}
            className={`${selectedCountry === (country.countryId || country.countryName) ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white border-gray-300 hover:bg-gray-50'} px-4 py-2 whitespace-nowrap`}
          >
            {country.countryName}
          </Button>
        ))}
        
    
      </div>
    );
  };

  // 渲染分页控件
  const renderPagination = () => {
    if (!newsData) return null;

    // 检查分页数据结构
    const pagination = newsData.pagination || newsData;
    const page = pagination.currentPage || pagination.page || 1;
    const totalPages = pagination.totalPages || pagination.pages || 1;
    
    // 如果没有分页信息或只有一页，不显示分页控件
    if (totalPages <= 1) return null;

    const pages = [];

    // 生成页码按钮
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === page ? 'primary' : 'outline'}
          size="sm"
          onClick={() => handlePageChange(i)}
          className={`${i === page ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="flex justify-center mt-8 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          className="bg-white border-gray-300 hover:bg-gray-50"
        >
          {t('international.pagination.previous')}
        </Button>
        {pages}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          className="bg-white border-gray-300 hover:bg-gray-50"
        >
          {t('international.pagination.next')}
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      {/* 页面头部 */}
      <div className="bg-blue-600 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center mb-4">{t('international.title')}</h1>
          <p className="text-center text-lg">{t('international.subtitle')}</p>
        </div>
      </div>

      {/* 主要内容 */}
      <div className="container mx-auto px-4 py-8">
        {/* 国家选择 */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('international.countrySelect.title')}</h2>
          {countriesLoading ? (
            <Loading />
          ) : (
            renderCountryButtons()
          )}
        </div>

        {/* 行业资讯 */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('international.industryNews.title')}</h2>
          
          {newsLoading ? (
            <Loading />
          ) : newsError ? (
            <div className="text-center py-12 text-red-500">
              {t('international.states.loadingFailed')}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(() => {
                  // 检查数据结构，资讯列表包含在records字段中
                  const newsArray = newsData?.records || [];
                  
                  if (newsArray.length === 0) {
                    return (
                      <div className="col-span-full text-center text-gray-500 py-12">
                        {t('international.states.noData')}
                      </div>
                    );
                  }
                  
                  return newsArray.map((news) => (
                    <Card key={news.newsId} hover className="h-full flex flex-col">
                      <div className="p-6 flex-grow">
                        <h3 className="font-semibold text-gray-900 text-lg mb-2">
                          {news.newsName}
                        </h3>
                        
                        <div className="mb-4">
                          <p className="text-gray-600 text-sm mb-4">
                            {news.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
                          </p>
                        </div>
                        
                        {news.hostPlace && (
                          <p className="text-gray-600 text-sm mb-2">
                            {t('international.industryNews.locationLabel')}{news.hostPlace}
                          </p>
                        )}
                        

                      </div>
                      
                      <div className="p-6 pt-0 border-t border-gray-100">
                        <Button
                          as={Link}
                          to={`/news/${news.newsId}`}
                          variant="primary"
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                        >
                          {t('international.industryNews.viewDetails')}
                        </Button>
                      </div>
                    </Card>
                  ));
                })()}
              </div>
              
              {/* 分页 */}
              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default InternationalPage;